import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Modal, message } from 'antd';
import ProxyBlock from '../components/proxy-block';
import DashboardDataBlock from '../components/dashboard-data-block';
import DashboardSimSummaryBlock from '../components/dashboard-sim-summary-block';
import ServerSummaryBlock from '../components/server-summary-block';
import UnavailableModemsBlock from '../components/unavailable-modems-block';
import CheckResultBanner from '../components/check-result-banner/CheckResultBanner';
import { apiService } from '../services/api';
import DashboardSalesPanel, {
  DashboardSalesDateRange,
  DashboardSalesMobileFilterSheet,
  DashboardSalesToolbar,
  SALES_DATA_SECTIONS_DEFAULT,
  resolveAutoChartGranularity,
  useDashboardSalesFilters,
} from '../components/dashboard-sales-panel';
import {
  buildModemRebootStateMap,
  isRebootCooldownActive,
  isSupportedModemRebootModel,
  resolveModemRebootModelName,
} from '../utils/modemRebootUtils';
import { formatMskApiDate } from '../utils/moscow-calendar-date';
import { computeConnectionForSaleModems } from '../utils/for-sale-stats';
import '../styles/dashboard.css';
import '../components/page-header-block/page-header-block.css';

const escapeRegex = (value) => String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const DASHBOARD_TABS = [
  { id: 'system', label: 'Система' },
  { id: 'sales', label: 'Продажи' },
];

const DashboardTabs = ({ activeTab, onTabChange, variant = 'default' }) => {
  const isHeaderVariant = variant === 'header';
  const tabsClassName = isHeaderVariant ? 'dashboard-header-row__tabs' : 'dashboard-tabs';
  const buttonClassName = isHeaderVariant ? 'dashboard-header-row__chip' : 'dashboard-tabs__button';
  const activeModifier = isHeaderVariant ? ' dashboard-header-row__chip--active' : ' dashboard-tabs__button--active';

  const handleTabClick = (event) => {
    const nextTabId = event.currentTarget.dataset.tabId;
    if (!nextTabId) return;
    onTabChange(nextTabId);
  };

  const handleTabKeyDown = (event) => {
    const currentIndex = DASHBOARD_TABS.findIndex((tab) => tab.id === activeTab);
    if (currentIndex < 0) return;

    const keyToIndex = {
      ArrowRight: (currentIndex + 1) % DASHBOARD_TABS.length,
      ArrowLeft: (currentIndex - 1 + DASHBOARD_TABS.length) % DASHBOARD_TABS.length,
      Home: 0,
      End: DASHBOARD_TABS.length - 1,
    };
    if (!(event.key in keyToIndex)) return;

    event.preventDefault();
    onTabChange(DASHBOARD_TABS[keyToIndex[event.key]].id);
  };

  const tabsContent = (
    <div className={tabsClassName} role="tablist" aria-label="Разделы дашборда">
      {DASHBOARD_TABS.map((tab) => (
        <button
          key={tab.id}
          type="button"
          id={`dashboard-tab-${tab.id}`}
          className={`${buttonClassName}${activeTab === tab.id ? activeModifier : ''}`}
          role="tab"
          aria-selected={activeTab === tab.id}
          aria-controls={`dashboard-panel-${tab.id}`}
          tabIndex={activeTab === tab.id ? 0 : -1}
          data-tab-id={tab.id}
          onClick={handleTabClick}
          onKeyDown={handleTabKeyDown}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );

  if (isHeaderVariant) {
    return <div className="dashboard-header-row__tabs-wrap">{tabsContent}</div>;
  }

  return tabsContent;
};

const isUnknownOperator = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  return normalized === '' || ['unknown', 'none', 'null', 'n/a', '-'].includes(normalized);
};

const normalizeOperatorTemplates = (rawTemplates) => {
  if (!Array.isArray(rawTemplates)) return [];
  return rawTemplates
    .filter((item) => item && typeof item === 'object')
    .map((item) => ({
      name: String(item.name || '').trim(),
      patterns: Array.isArray(item.patterns)
        ? item.patterns.map((pattern) => String(pattern || '').trim()).filter(Boolean)
        : [],
    }))
    .filter((item) => item.name);
};

const hasPositiveAllocationWeights = (weights) =>
  Object.values(weights || {}).some((value) => Number(value || 0) > 0);

const pickAllocationWeights = (...candidates) => {
  for (const candidate of candidates) {
    if (hasPositiveAllocationWeights(candidate)) {
      return candidate;
    }
  }
  return {};
};

const allocateIntegerByWeights = (total, weights) => {
  const normalizedTotal = Math.max(0, Math.trunc(Number(total) || 0));
  if (normalizedTotal === 0) {
    return {};
  }

  const entries = Object.entries(weights || {})
    .map(([key, value]) => [key, Math.max(0, Number(value) || 0)])
    .filter(([, value]) => value > 0);

  if (entries.length === 0) {
    return {};
  }

  const totalWeight = entries.reduce((sum, [, value]) => sum + value, 0);
  if (totalWeight <= 0) {
    return {};
  }

  const exactAllocations = entries.map(([key, value]) => {
    const exact = (normalizedTotal * value) / totalWeight;
    const base = Math.floor(exact);
    return {
      key,
      value,
      base,
      fraction: exact - base,
    };
  });

  let remaining = normalizedTotal - exactAllocations.reduce((sum, item) => sum + item.base, 0);
  exactAllocations.sort((a, b) => {
    if (b.fraction !== a.fraction) return b.fraction - a.fraction;
    if (b.value !== a.value) return b.value - a.value;
    return String(a.key).localeCompare(String(b.key), 'ru');
  });

  const result = exactAllocations.reduce((acc, item) => ({
    ...acc,
    [item.key]: item.base,
  }), {});

  for (let index = 0; index < exactAllocations.length && remaining > 0; index += 1) {
    result[exactAllocations[index].key] += 1;
    remaining -= 1;
  }

  return result;
};

const resolveOperatorByTemplates = (rawOperator, templates) => {
  const value = String(rawOperator || '').trim();
  if (isUnknownOperator(value)) return 'UNKNOWN';
  const preparedTemplates = normalizeOperatorTemplates(templates);
  if (preparedTemplates.length === 0) return value;

  for (const template of preparedTemplates) {
    for (const pattern of template.patterns) {
      try {
        const regex = new RegExp(`^${escapeRegex(pattern).replace(/\\\*/g, '.*')}$`, 'i');
        if (regex.test(value)) return template.name;
      } catch {
        continue;
      }
    }
  }

  return value;
};

/** Достаёт JSON-объект статистики из ответа axios (и защита от HTML/строки при 404 прокси). */
const parseStatsPayloadFromApi = (axiosResponse) => {
  if (!axiosResponse || typeof axiosResponse !== 'object') return null;
  const raw = axiosResponse.data !== undefined ? axiosResponse.data : axiosResponse;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  if (typeof raw.data === 'object' && raw.data !== null && !Array.isArray(raw.data)) {
    const inner = raw.data;
    if ('total_proxies' in inner || 'available_modems' in inner) {
      return inner;
    }
  }
  return raw;
};

/** JSON статистики SIM из ответа axios (тот же источник, что Sim Manager — /api/sim-cards). */
const parseSimCardsStatsPayload = (axiosResponse) => {
  const raw = axiosResponse?.data !== undefined ? axiosResponse.data : axiosResponse;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  if (typeof raw.data === 'object' && raw.data !== null && !Array.isArray(raw.data)) {
    const inner = raw.data;
    if ('total' in inner || 'sim_dashboard_by_operator' in inner) {
      return inner;
    }
  }
  if ('total' in raw || 'sim_dashboard_by_operator' in raw) {
    return raw;
  }
  return null;
};

/** Есть ли в payload хоть какие-то ненулевые счётчики (прокси/серверы/модемы). */
const hasMeaningfulStatsCounts = (payload) => {
  if (!payload || typeof payload !== 'object') return false;
  const tp = Number(payload.total_proxies);
  const ts = Number(payload.total_servers);
  const tm = Number(payload.total_modems);
  const am = Number(payload.available_modems);
  const um = Number(payload.unavailable_modems);
  return tp > 0 || ts > 0 || tm > 0 || am > 0 || um > 0;
};

/** Поля, которые можно подмешать из «пустого» ответа, не затирая счётчики (обогащение). */
const STATS_ENRICHMENT_KEYS = [
  'unavailable_modems_list',
  'ports_stats',
  'servers_summary',
];

const FREE_MODEM_KEYS = ['free_modems', 'free_modems_spb', 'free_modems_moscow'];

const sumFreeModemKeys = (obj) =>
  FREE_MODEM_KEYS.reduce((acc, k) => acc + Number(obj?.[k] ?? 0), 0);

/**
 * Сливает новый ответ статистики с предыдущим: нулевой/ошибочный ответ не затирает уже показанные цифры.
 * Иначе тяжёлый /api/stats?include_unavailable_details=true может вернуть нули и мгновенно обнулить UI.
 * Отдельно: не затираем блок «сводные модемы» нулями из лёгкого summary (раньше там всегда были 0).
 */
const mergeStatsPayload = (prev, incoming) => {
  if (!incoming || typeof incoming !== 'object') {
    return prev ?? null;
  }
  if (!prev || typeof prev !== 'object') {
    return incoming;
  }
  if (hasMeaningfulStatsCounts(incoming)) {
    const merged = { ...prev, ...incoming };
    const incFree = sumFreeModemKeys(incoming);
    const prevFree = sumFreeModemKeys(prev);
    if (incFree === 0 && prevFree > 0) {
      for (const k of FREE_MODEM_KEYS) {
        merged[k] = prev[k];
      }
    }
    // Тяжёлый ответ часто приходит вторым; пустой список не должен затирать строки таблицы «Недоступные модемы»
    const prevUm = prev.unavailable_modems_list;
    const incUm = incoming.unavailable_modems_list;
    const prevLen = Array.isArray(prevUm) ? prevUm.length : 0;
    const incLen = Array.isArray(incUm) ? incUm.length : 0;
    if (prevLen > 0 && incLen === 0) {
      merged.unavailable_modems_list = prevUm;
    }
    return merged;
  }
  const patch = {};
  for (const key of STATS_ENRICHMENT_KEYS) {
    if (incoming[key] !== undefined) {
      patch[key] = incoming[key];
    }
  }
  if (sumFreeModemKeys(incoming) > 0) {
    for (const k of FREE_MODEM_KEYS) {
      if (incoming[k] !== undefined) {
        patch[k] = incoming[k];
      }
    }
  }
  if (incoming.revenue && typeof incoming.revenue === 'object') {
    patch.revenue = incoming.revenue;
  }
  return Object.keys(patch).length > 0 ? { ...prev, ...patch } : prev;
};

const formatSalesApiDate = formatMskApiDate;

const parseSalesPayloadFromApi = (axiosResponse) => {
  if (!axiosResponse || typeof axiosResponse !== 'object') {
    return null;
  }
  const raw = axiosResponse.data !== undefined ? axiosResponse.data : axiosResponse;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return null;
  }
  if (raw.data === null) {
    return null;
  }
  if (typeof raw.data === 'object' && raw.data !== null && !Array.isArray(raw.data)) {
    return raw.data;
  }
  return raw;
};

const DASHBOARD_MOBILE_MAX_WIDTH = 768;

const Dashboard = () => {
  const [activeDashboardTab, setActiveDashboardTab] = useState('system');
  const [stats, setStats] = useState(null);
  const [connections, setConnections] = useState([]);
  const [operatorTemplates, setOperatorTemplates] = useState([]);
  const [simStats, setSimStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [recheckLoading, setRecheckLoading] = useState(false);
  const [dashboardModemRebootLoading, setDashboardModemRebootLoading] = useState(false);
  const [isSalesMobileFiltersOpen, setIsSalesMobileFiltersOpen] = useState(false);
  const [visibleSalesDataSections, setVisibleSalesDataSections] = useState(
    () => [...SALES_DATA_SECTIONS_DEFAULT],
  );
  const [isMobileViewport, setIsMobileViewport] = useState(() => (
    typeof window !== 'undefined'
    && window.matchMedia(`(max-width: ${DASHBOARD_MOBILE_MAX_WIDTH}px)`).matches
  ));
  const [operatorTableSort, setOperatorTableSort] = useState({ columnIndex: null, direction: 'desc' });
  /** Системный склад (Mbps) по операторам для колонок Общий/Общий+/Индивидуальный */
  const [operatorWarehouseStock, setOperatorWarehouseStock] = useState({ rows: [], totals: null });
  const [modemMismatchRows, setModemMismatchRows] = useState([]);
  const [modemMismatchLoading, setModemMismatchLoading] = useState(false);
  const [modemMismatchCheckLoading, setModemMismatchCheckLoading] = useState(false);
  const [modemMismatchApplyingRowId, setModemMismatchApplyingRowId] = useState(null);
  const [modemMismatchBulkApplying, setModemMismatchBulkApplying] = useState(false);
  const [selectedDefaultMismatchModemIds, setSelectedDefaultMismatchModemIds] = useState([]);
  const [proxyCheckSummary, setProxyCheckSummary] = useState(null);
  const [connectionsLoading, setConnectionsLoading] = useState(false);
  const [modemRebootStateById, setModemRebootStateById] = useState({});
  const [krakenHostStats, setKrakenHostStats] = useState([]);
  const [salesDataByGranularity, setSalesDataByGranularity] = useState({});
  const [salesLoading, setSalesLoading] = useState(false);
  const [manualRefreshInFlight, setManualRefreshInFlight] = useState(false);
  const lastDashboardModemDonutStatsRef = React.useRef(null);
  const salesDataByGranularityRef = React.useRef({});
  const salesFilters = useDashboardSalesFilters();
  const autoChartGranularity = useMemo(
    () => resolveAutoChartGranularity(
      salesFilters.periodFilter,
      salesFilters.rangeStart,
      salesFilters.rangeEnd,
    ),
    [salesFilters.periodFilter, salesFilters.rangeStart, salesFilters.rangeEnd],
  );
  const salesData = (
    salesDataByGranularity[autoChartGranularity]
    || Object.values(salesDataByGranularity)[0]
    || null
  );

  useEffect(() => {
    salesDataByGranularityRef.current = salesDataByGranularity;
  }, [salesDataByGranularity]);

  const fetchKrakenHostResources = useCallback(async () => {
    try {
      const response = await apiService.getKrakenHostResources();
      const stats = response?.data?.stats ?? response?.stats;
      setKrakenHostStats(Array.isArray(stats) ? stats : []);
    } catch (_e) {
      setKrakenHostStats([]);
    }
  }, []);

  const fetchConnections = useCallback(async () => {
    try {
      const response = await apiService.getKrakenConnections();
      setConnections(response?.data?.connections || []);
    } catch (_error) {
      setConnections([]);
    }
  }, []);

  const fetchOperatorTemplates = useCallback(async () => {
    try {
      const response = await apiService.getOperatorTemplates();
      const rawTemplates =
        response?.data?.data?.templates ||
        response?.data?.templates ||
        response?.data ||
        [];
      setOperatorTemplates(normalizeOperatorTemplates(rawTemplates));
    } catch (_error) {
      setOperatorTemplates([]);
    }
  }, []);

  const fetchOperatorWarehouseStock = useCallback(async () => {
    try {
      const response = await apiService.getDashboardOperatorWarehouseStock();
      const payload = response?.data?.data || response?.data || {};
      const rows = Array.isArray(payload.rows) ? payload.rows : [];
      const totals = payload.totals && typeof payload.totals === 'object' ? payload.totals : null;
      setOperatorWarehouseStock({ rows, totals });
    } catch (_error) {
      setOperatorWarehouseStock({ rows: [], totals: null });
    }
  }, []);

  const normalizeMismatchRows = useCallback((response) => {
    const directRows = response?.data?.rows;
    const nestedRows = response?.data?.data?.rows;
    const rows = directRows || nestedRows;
    if (!Array.isArray(rows)) return [];
    return rows;
  }, []);

  const fetchModemMismatchRows = useCallback(async (isBackground = false) => {
    try {
      if (!isBackground) {
        setModemMismatchLoading(true);
      }
      const response = await apiService.getDashboardModemSettingsMismatches();
      const normalizedRows = normalizeMismatchRows(response);
      setModemMismatchRows(normalizedRows);
    } catch (_error) {
      if (!isBackground) {
        setModemMismatchRows([]);
      }
    } finally {
      if (!isBackground) {
        setModemMismatchLoading(false);
      }
    }
  }, [normalizeMismatchRows]);

  const fetchSimStatsInBackground = useCallback(async () => {
    try {
      const res = await apiService.getSimCardsStats();
      const payload = parseSimCardsStatsPayload(res);
      if (payload) {
        setSimStats(payload);
      }
    } catch (_e) {
      /* не затираем уже показанную сводку при временной ошибке */
    }
  }, []);

  const fetchFullStatsInBackground = useCallback(async () => {
    try {
      const fullRes = await apiService.getStats({ include_unavailable_details: true });
      const fullData = parseStatsPayloadFromApi(fullRes);
      if (fullData) {
        setStats((prev) => mergeStatsPayload(prev, fullData));
      }
    } catch (_) {
      /* оставляем уже показанный summary/light */
    }
  }, []);

  const fetchLightStatsPayload = useCallback(async () => {
    // Сначала лёгкий summary — не блокируем UI тяжёлым get_proxy_stats.
    try {
      const summaryRes = await apiService.getStatsSummary();
      const summaryPayload = parseStatsPayloadFromApi(summaryRes);
      if (hasMeaningfulStatsCounts(summaryPayload)) {
        return summaryPayload;
      }
    } catch (_summaryErr) {
      /* fallback на /api/stats ниже */
    }

    try {
      const statsResponse = await apiService.getStats({ include_unavailable_details: false });
      return parseStatsPayloadFromApi(statsResponse);
    } catch (e) {
      const err = new Error(e?.message || 'Ошибка загрузки статистики');
      err.cause = e;
      throw err;
    }
  }, []);

  const fetchData = useCallback(async (isBackground = false, options = {}) => {
    const replaceStats = Boolean(options && options.replaceStats);
    try {
      if (!isBackground) {
        setLoading(true);
      }
      setError(null);

      // Connections/host — сразу, без ожидания /api/stats (источник «Модемы» + «Сводка по серверам»).
      if (!isBackground) {
        setConnectionsLoading(true);
      }
      const connectionsPromise = Promise.all([
        fetchConnections(),
        fetchKrakenHostResources(),
      ]).finally(() => {
        if (!isBackground) {
          setConnectionsLoading(false);
        }
      });

      void fetchOperatorTemplates();
      void fetchOperatorWarehouseStock();
      void fetchModemMismatchRows(isBackground);
      void fetchSimStatsInBackground();

      let statsPayload = null;
      try {
        if (replaceStats) {
          const fullRes = await apiService.getStats({ include_unavailable_details: true });
          statsPayload = parseStatsPayloadFromApi(fullRes);
        } else {
          statsPayload = await fetchLightStatsPayload();
        }
      } catch (e) {
        if (!isBackground) {
          throw e;
        }
      }
      if (statsPayload) {
        setStats((prev) => (replaceStats ? statsPayload : mergeStatsPayload(prev, statsPayload)));
      }

      if (!isBackground) {
        setLoading(false);
      }

      // Полный /api/stats (unavailable details) — только фоном, не блокирует блоки.
      if (!replaceStats) {
        void fetchFullStatsInBackground();
      }

      if (isBackground) {
        await Promise.allSettled([connectionsPromise]);
      }
    } catch (err) {
      if (!isBackground) {
        setError(err.message || 'Ошибка загрузки данных');
      }
    } finally {
      if (!isBackground) {
        setLoading(false);
      }
    }
  }, [
    fetchConnections,
    fetchKrakenHostResources,
    fetchModemMismatchRows,
    fetchOperatorTemplates,
    fetchOperatorWarehouseStock,
    fetchSimStatsInBackground,
    fetchLightStatsPayload,
    fetchFullStatsInBackground,
  ]);

  const fetchSalesDataForGranularity = useCallback(async (
    chartGranularity,
    { replaceCache = false } = {},
  ) => {
    const granularityKey = chartGranularity || autoChartGranularity;
    setSalesLoading(true);
    try {
      const dateFrom = formatSalesApiDate(salesFilters.rangeStart);
      const dateTo = formatSalesApiDate(salesFilters.rangeEnd);
      const response = await apiService.getDashboardSales({
        date_from: dateFrom,
        date_to: dateTo,
        geo: salesFilters.geoFilter,
        period: salesFilters.periodFilter,
        chart_granularity: granularityKey,
      });
      let payload = parseSalesPayloadFromApi(response);
      if (!payload) {
        await new Promise((resolve) => {
          window.setTimeout(resolve, 2500);
        });
        const retryResponse = await apiService.getDashboardSales({
          date_from: dateFrom,
          date_to: dateTo,
          geo: salesFilters.geoFilter,
          period: salesFilters.periodFilter,
          chart_granularity: granularityKey,
        });
        payload = parseSalesPayloadFromApi(retryResponse);
      }
      if (payload) {
        setSalesDataByGranularity((prev) => (
          replaceCache
            ? { [granularityKey]: payload }
            : { ...prev, [granularityKey]: payload }
        ));
      }
      return payload;
    } catch (_error) {
      /* сохраняем предыдущие данные продаж при временной ошибке */
      return null;
    } finally {
      setSalesLoading(false);
    }
  }, [
    autoChartGranularity,
    salesFilters.geoFilter,
    salesFilters.periodFilter,
    salesFilters.rangeEnd,
    salesFilters.rangeStart,
  ]);

  const ensureSalesChartGranularity = useCallback(async (chartGranularity) => {
    const granularityKey = chartGranularity || autoChartGranularity;
    if (salesDataByGranularityRef.current[granularityKey]) {
      return salesDataByGranularityRef.current[granularityKey];
    }
    return fetchSalesDataForGranularity(granularityKey);
  }, [autoChartGranularity, fetchSalesDataForGranularity]);

  const fetchSalesData = useCallback(() => (
    fetchSalesDataForGranularity(autoChartGranularity, { replaceCache: true })
  ), [autoChartGranularity, fetchSalesDataForGranularity]);

  useEffect(() => {
    if (activeDashboardTab !== 'sales') {
      return undefined;
    }
    void fetchSalesDataForGranularity(autoChartGranularity, { replaceCache: true });
    return undefined;
  }, [
    activeDashboardTab,
    autoChartGranularity,
    fetchSalesDataForGranularity,
  ]);

  useEffect(() => {
    if (activeDashboardTab === 'sales') {
      return undefined;
    }
    setIsSalesMobileFiltersOpen(false);
    return undefined;
  }, [activeDashboardTab]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }
    const mediaQuery = window.matchMedia(`(max-width: ${DASHBOARD_MOBILE_MAX_WIDTH}px)`);
    const handleViewportChange = () => {
      setIsMobileViewport(mediaQuery.matches);
    };
    handleViewportChange();
    mediaQuery.addEventListener('change', handleViewportChange);
    return () => mediaQuery.removeEventListener('change', handleViewportChange);
  }, []);

  const handleCloseSalesMobileFilters = useCallback(() => {
    setIsSalesMobileFiltersOpen(false);
  }, []);

  const handleToggleSalesMobileFilters = () => {
    setIsSalesMobileFiltersOpen((prev) => !prev);
  };

  const handleToggleSalesMobileFiltersKeyDown = (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }
    event.preventDefault();
    handleToggleSalesMobileFilters();
  };

  const handleSalesMobileFiltersApply = useCallback(({ geoFilter, periodFilter, visibleDataSections }) => {
    salesFilters.setGeoFilter(geoFilter);
    if (periodFilter !== salesFilters.periodFilter) {
      salesFilters.handlePeriodFilterChange(periodFilter);
    } else {
      salesFilters.setPeriodFilter(periodFilter);
    }
    setVisibleSalesDataSections(visibleDataSections);
    setIsSalesMobileFiltersOpen(false);
  }, [salesFilters]);

  const handleSalesMobileFiltersReset = useCallback(() => {
    salesFilters.resetFilters();
    setVisibleSalesDataSections([...SALES_DATA_SECTIONS_DEFAULT]);
    setIsSalesMobileFiltersOpen(false);
  }, [salesFilters]);

  useEffect(() => {
    fetchData();

    // Автообновление каждые 2 минуты пока дашборд открыт
    const intervalId = setInterval(() => {
      fetchData(true);
    }, 120000);

    return () => clearInterval(intervalId);
  }, [fetchData]);

  const handleDashboardTabChange = useCallback((tabId) => {
    setActiveDashboardTab(tabId);
  }, []);

  // Быстрая проверка всех прокси через API
  const handleRecheck = useCallback(async () => {
    try {
      setRecheckLoading(true);
      const response = await apiService.quickCheckAllProxies();
      const payload = response?.data?.data || response?.data || {};
      const totalChecked = Number(payload.total_checked || 0);
      const available = Number(payload.available || 0);
      const unavailable = Number(payload.unavailable || 0);
      const errors = Number(payload.errors || 0);
      setProxyCheckSummary({
        tone: errors > 0 ? 'error' : 'success',
        text: `Проверка прокси: проверено ${totalChecked}, доступно ${available}, недоступно ${unavailable}, ошибок ${errors}`,
      });
      // После проверки обновляем статистику
      await fetchData();
    } catch (err) {
      setProxyCheckSummary({
        tone: 'error',
        text: `Ошибка проверки прокси: ${err?.response?.data?.detail || err.message || 'неизвестно'}`,
      });
    } finally {
      setRecheckLoading(false);
    }
  }, [fetchData]);

  const handleCheckModemMismatch = useCallback(async () => {
    try {
      setModemMismatchCheckLoading(true);
      const response = await apiService.checkDashboardModemSettingsMismatches();
      const nextRows = normalizeMismatchRows(response);
      if (Array.isArray(nextRows)) {
        setModemMismatchRows(nextRows);
      }
      await fetchModemMismatchRows(true);
    } catch (_error) {
      message.error('Ошибка ручной проверки рассинхронов');
      await fetchModemMismatchRows(true);
    } finally {
      setModemMismatchCheckLoading(false);
    }
  }, [fetchModemMismatchRows, normalizeMismatchRows]);

  const handleApplyMismatchSettings = useCallback(async (row) => {
    const rowType = String(row?.row_type || 'proxy_mismatch');
    const proxyId = Number(row?.proxy_id || 0);
    const modemId = Number(row?.modem_id || 0);
    const rowId = row?.row_id || (rowType === 'modem_default_mismatch' ? `modem-${modemId}` : `proxy-${proxyId}`);
    try {
      setModemMismatchApplyingRowId(rowId);
      if (rowType === 'modem_default_mismatch') {
        if (!modemId) {
          message.error('Не удалось определить модем для применения');
          return;
        }
        await apiService.applyDashboardModemDefaultMismatch(modemId);
        message.success(`Дефолтные настройки применены к модему #${modemId}`);
      } else {
        if (!proxyId) {
          message.error('Не удалось определить прокси для применения');
          return;
        }
        await apiService.applyDashboardModemSettingsMismatch(proxyId);
        message.success(`Настройки модема применены для прокси #${proxyId}`);
      }
      await fetchModemMismatchRows(true);
    } catch (_error) {
      const backendDetail = _error?.response?.data?.detail;
      if (rowType === 'modem_default_mismatch') {
        message.error(backendDetail || `Не удалось применить дефолт для модема #${modemId || '—'}`);
      } else {
        message.error(backendDetail || `Не удалось применить настройки для прокси #${proxyId || '—'}`);
      }
    } finally {
      setModemMismatchApplyingRowId(null);
    }
  }, [fetchModemMismatchRows]);

  useEffect(() => {
    const availableDefaultModemIds = new Set(
      modemMismatchRows
        .filter((row) => String(row?.row_type || 'proxy_mismatch') === 'modem_default_mismatch')
        .map((row) => Number(row?.modem_id || 0))
        .filter((modemId) => modemId > 0)
    );
    setSelectedDefaultMismatchModemIds((prevSelected) =>
      prevSelected.filter((modemId) => availableDefaultModemIds.has(modemId))
    );
  }, [modemMismatchRows]);

  const handleToggleDefaultModemSelection = useCallback((modemId, isSelected) => {
    const normalizedModemId = Number(modemId || 0);
    if (!normalizedModemId) return;
    setSelectedDefaultMismatchModemIds((prevSelected) => {
      if (isSelected) {
        if (prevSelected.includes(normalizedModemId)) return prevSelected;
        return [...prevSelected, normalizedModemId];
      }
      return prevSelected.filter((item) => item !== normalizedModemId);
    });
  }, []);

  const defaultMismatchRows = modemMismatchRows.filter(
    (row) => String(row?.row_type || 'proxy_mismatch') === 'modem_default_mismatch'
  );
  const defaultMismatchModemIds = defaultMismatchRows
    .map((row) => Number(row?.modem_id || 0))
    .filter((modemId) => modemId > 0);
  const allDefaultRowsSelected =
    defaultMismatchModemIds.length > 0 &&
    defaultMismatchModemIds.every((modemId) => selectedDefaultMismatchModemIds.includes(modemId));

  const handleToggleSelectAllDefaults = useCallback((isSelected) => {
    if (!isSelected) {
      setSelectedDefaultMismatchModemIds([]);
      return;
    }
    const modemIds = modemMismatchRows
      .filter((row) => String(row?.row_type || 'proxy_mismatch') === 'modem_default_mismatch')
      .map((row) => Number(row?.modem_id || 0))
      .filter((modemId) => modemId > 0);
    setSelectedDefaultMismatchModemIds(Array.from(new Set(modemIds)));
  }, [modemMismatchRows]);

  const handleApplySelectedDefaults = useCallback(async () => {
    const modemIds = Array.from(new Set(selectedDefaultMismatchModemIds)).filter((modemId) => Number(modemId) > 0);
    if (modemIds.length === 0) {
      message.warning('Выбери хотя бы один модем с дефолтным рассинхроном');
      return;
    }
    try {
      setModemMismatchBulkApplying(true);
      const response = await apiService.applyDashboardModemDefaultsBulk(modemIds);
      const data = response?.data || {};
      const appliedCount = Number(data?.applied_count || 0);
      const failedCount = Number(data?.failed_count || 0);
      if (appliedCount > 0 && failedCount === 0) {
        message.success(`Дефолт применен к ${appliedCount} модемам`);
      } else if (appliedCount > 0 && failedCount > 0) {
        message.warning(`Применено: ${appliedCount}, с ошибкой: ${failedCount}`);
      } else {
        message.error('Не удалось применить дефолты к выбранным модемам');
      }
      setSelectedDefaultMismatchModemIds([]);
      await fetchModemMismatchRows(true);
    } catch (_error) {
      message.error('Ошибка массового применения дефолтных настроек');
    } finally {
      setModemMismatchBulkApplying(false);
    }
  }, [fetchModemMismatchRows, selectedDefaultMismatchModemIds]);

  // Сокращение названий городов для столбца ГЕО
  const shortenGeo = (city) => {
    if (!city) return '—';
    const lower = city.toLowerCase();
    if (lower.includes('петербург') || lower.includes('спб') || lower === 'spb') return 'СПб';
    if (lower.includes('москва') || lower.includes('мск') || lower === 'moscow' || lower === 'msk') return 'МСК';
    return city;
  };

  // Маппинг подключений к формату ServerSummaryBlock
  const mergeKrakenHostResources = (serversList, hostStats) => {
    const byId = {};
    (hostStats || []).forEach((s) => {
      if (s && s.connection_id != null) {
        byId[s.connection_id] = s;
      }
    });
    return serversList.map((srv) => {
      const h = byId[srv.id];
      if (!h || h.error) {
        return {
          ...srv,
          hostCpu: null,
          hostMemoryPercent: null,
          hostMemoryLabel: null,
          hostUptimeHumanize: null,
        };
      }
      const mem = h.memory || {};
      const total = Number(mem.total) || 0;
      const used = Number(mem.used) || 0;
      const memPct = total > 0 ? Math.round((used / total) * 100) : null;
      return {
        ...srv,
        hostCpu: h.cpu != null ? Math.round(Number(h.cpu) * 10) / 10 : null,
        hostMemoryPercent: memPct,
        hostMemoryLabel: total > 0 ? `${used} / ${total} МБ` : null,
        hostUptimeHumanize: h.uptime?.humanize || null,
      };
    });
  };

  /** Те же критерии, что у круговой диаграммы «Модемы» (modemStatsEligiblePorts). */
  const isModemStatsEligiblePort = (port) => {
    const live = port?.kraken_connection_live;
    if (live === false || live === 0) {
      return false;
    }
    if (port?.modem_offline === true) {
      return false;
    }
    if (port?.active === false) {
      return false;
    }
    return true;
  };

  const countVisibleModemsByConnection = (portsList) => {
    const counts = {};
    if (!Array.isArray(portsList)) {
      return counts;
    }
    portsList.forEach((port) => {
      if (!isModemStatsEligiblePort(port)) {
        return;
      }
      const connectionId = port?.connection_id;
      if (connectionId == null) {
        return;
      }
      counts[connectionId] = (counts[connectionId] || 0) + 1;
    });
    return counts;
  };

  const mapConnections = (connectionsList, portsList) => {
    if (!connectionsList || !Array.isArray(connectionsList)) {
      return [];
    }
    const visibleModemsByConnection = countVisibleModemsByConnection(portsList);
    return connectionsList
      .filter((conn) => conn && conn.is_active !== false)
      .map((conn) => {
      const fromPorts = visibleModemsByConnection[conn.id];
      // Источник истины — servers_count/modems_count из API (count_visible_kraken_modems_by_connection).
      const totalModems = Number(
        conn.servers_count ?? conn.modems_count ?? fromPorts ?? 0
      );
      const totalProxies = conn.proxies_count || 0;
      const freeModems = conn.free_modems || 0;
      const unavailableModems = conn.unavailable_modems || 0;
      const reserveModems = Number(conn.reserve_modems_count) || 0;
      const forSaleModems = computeConnectionForSaleModems(conn);
      const loadPercent = conn.overall_load_percentage != null
        ? Math.round(conn.overall_load_percentage)
        : 0;
      
      return {
        id: conn.id,
        name: conn.name || `Подключение ${conn.id}`,
        geo: shortenGeo(conn.city),
        totalModems: totalModems,
        freeModems: freeModems,
        unavailableModems,
        reserveModems,
        forSaleModems,
        totalProxies: totalProxies,
        unavailableProxies: conn.unavailable_proxies || 0,
        load: Math.min(loadPercent, 100),
        totalAllocatedSpeed: conn.total_allocated_speed || 0,
        totalCapacity: conn.total_capacity || 0,
        selected: false,
      };
    });
  };

  // Нормализация enum-значений оператора из БД в читаемые названия
  const normalizeOperator = (op) => {
    const resolvedOperator = resolveOperatorByTemplates(op, operatorTemplates);
    if (!resolvedOperator || resolvedOperator === 'UNKNOWN') return '—';

    const upper = String(resolvedOperator).toUpperCase();
    if (upper === 'MTS') return 'МТС';
    if (upper === 'MEGAFON') return 'МегаФон';
    if (upper === 'BEELINE') return 'Билайн';
    if (upper === 'TELE2') return 'Tele2';
    if (upper === 'YOTA') return 'Yota';
    if (upper === 'ROSTELECOM') return 'Ростелеком';
    if (upper === 'MOTIV') return 'Мотив';
    return resolvedOperator;
  };

  const mapOperatorNameToKey = (rawOperator) => {
    const value = String(resolveOperatorByTemplates(rawOperator, operatorTemplates) || '').trim().toLowerCase();
    if (!value) return null;
    if (value.includes('beeline') || value.includes('билайн')) return 'beeline';
    if (value.includes('megafon') || value.includes('мегафон')) return 'megafon';
    if (value.includes('tele2') || value.includes('т2') || value.includes('t2') || value.includes('теле2')) return 't2';
    if (value.includes('yota')) return 'yota';
    if (value.includes('mts') || value.includes('мтс')) return 'mts';
    return null;
  };

  const mapCityToKey = (rawCity) => {
    const value = String(rawCity || '').trim().toLowerCase();
    // Как в proxy_manager.compute_free_modems_by_city — иначе «ЦОД Мск» и т.п. не попадают в Мск,
    // ломается reserveMsk / «В продаже Мск» (доля резерва считается по opNMsk/tot).
    if (
      value.includes('петербург')
      || value.includes('питер')
      || value.includes('спб')
      || value.includes('spb')
      || value.includes('saint petersburg')
    ) {
      return 'spb';
    }
    if (
      value.includes('москва')
      || value.includes('мск')
      || value.includes('msk')
      || value.includes('moscow')
    ) {
      return 'msk';
    }
    return null;
  };

  const isOperatorPortKrakenLive = (port) => {
    const v = port?.kraken_connection_live;
    if (v === false || v === 0) return false;
    return true;
  };

  /**
   * Сводка по операторам.
   * Число «В продаже» должно совпадать с connection-level сводкой из ServerSummaryBlock,
   * поэтому сначала считаем итог по подключению, а затем раскладываем его по операторам
   * пропорционально free-модемам без потерь из-за Math.round.
   */
  const buildOperatorsSummaryRows = (dashboardStats, portsOverride = [], connectionsList = []) => {
    const operatorOrder = [
      { key: 'beeline', label: 'Билайн' },
      { key: 'mts', label: 'МТС' },
      { key: 'megafon', label: 'Мегафон' },
      { key: 't2', label: 'Теле2' },
      { key: 'yota', label: 'Yota' },
    ];

    const counters = operatorOrder.reduce((acc, item) => {
      acc[item.key] = {
        total: 0,
        spbTotal: 0,
        spbFree: 0,
        mskTotal: 0,
        mskFree: 0,
        freeSum: 0,
        reserveSum: 0,
        reserveSpbSum: 0,
        reserveMskSum: 0,
        forSaleSum: 0,
        forSaleSpbSum: 0,
        forSaleMskSum: 0,
      };
      return acc;
    }, {});

    const activeConnections = Array.isArray(connectionsList)
      ? connectionsList.filter((conn) => conn && conn.is_active !== false)
      : [];

    const hasOverridePorts = Array.isArray(portsOverride) && portsOverride.length > 0;
    const portsRaw = hasOverridePorts
      ? portsOverride
      : (Array.isArray(dashboardStats?.ports_stats) ? dashboardStats.ports_stats : []);

    const ports = hasOverridePorts
      ? portsRaw.filter((p) => isOperatorPortKrakenLive(p))
      : portsRaw;

    if (ports.length > 0) {
      const connModemsByOp = {};
      const connFreeByOp = {};
      const connFreeByOpSpb = {};
      const connFreeByOpMsk = {};

      const incrementConnectionOperatorCounter = (targetMap, connectionId, operatorKey) => {
        if (!Number.isFinite(connectionId) || connectionId <= 0 || !operatorKey) return;
        if (!targetMap[connectionId]) targetMap[connectionId] = {};
        targetMap[connectionId][operatorKey] = (targetMap[connectionId][operatorKey] || 0) + 1;
      };

      ports.forEach((port) => {
        if (!port || typeof port !== 'object') return;
        const operatorKey = mapOperatorNameToKey(port.operator || port.mobile_operator || port.selected_operator);
        if (!operatorKey || !counters[operatorKey]) return;

        const cityKey = mapCityToKey(port.city || port.connection_city || port.server_city || port.geo);
        const loadRaw = port.overall_load_percentage ?? port.utilization;
        const hasLoadValue = loadRaw !== undefined && loadRaw !== null;
        const loadValue = hasLoadValue ? Number(loadRaw) : NaN;
        const isFree = hasLoadValue && Number.isFinite(loadValue) && loadValue < 0.1;

        counters[operatorKey].total += 1;
        if (isFree) counters[operatorKey].freeSum += 1;

        if (cityKey === 'spb') {
          counters[operatorKey].spbTotal += 1;
          if (isFree) counters[operatorKey].spbFree += 1;
        }
        if (cityKey === 'msk') {
          counters[operatorKey].mskTotal += 1;
          if (isFree) counters[operatorKey].mskFree += 1;
        }

        const cid = Number(port.connection_id ?? port.kraken_connection_id);
        if (Number.isFinite(cid) && cid > 0) {
          incrementConnectionOperatorCounter(connModemsByOp, cid, operatorKey);
          if (isFree) {
            incrementConnectionOperatorCounter(connFreeByOp, cid, operatorKey);
            if (cityKey === 'spb') incrementConnectionOperatorCounter(connFreeByOpSpb, cid, operatorKey);
            if (cityKey === 'msk') incrementConnectionOperatorCounter(connFreeByOpMsk, cid, operatorKey);
          }
        }
      });

      activeConnections.forEach((connection) => {
        const connectionId = Number(connection?.id);
        if (!Number.isFinite(connectionId) || connectionId <= 0) {
          return;
        }

        const forSaleModems = computeConnectionForSaleModems(connection);
        if (forSaleModems === 0) {
          return;
        }

        const overallWeights = pickAllocationWeights(
          connFreeByOp[connectionId],
          connModemsByOp[connectionId]
        );
        const overallAllocation = allocateIntegerByWeights(forSaleModems, overallWeights);
        Object.entries(overallAllocation).forEach(([operatorKey, value]) => {
          if (counters[operatorKey]) {
            counters[operatorKey].forSaleSum += value;
          }
        });

        const connectionCityKey = mapCityToKey(connection?.city);
        if (connectionCityKey === 'spb' || connectionCityKey === 'msk') {
          const cityWeights = connectionCityKey === 'spb'
            ? pickAllocationWeights(connFreeByOpSpb[connectionId], connFreeByOp[connectionId], connModemsByOp[connectionId])
            : pickAllocationWeights(connFreeByOpMsk[connectionId], connFreeByOp[connectionId], connModemsByOp[connectionId]);

          const cityAllocation = allocateIntegerByWeights(forSaleModems, cityWeights);
          Object.entries(cityAllocation).forEach(([operatorKey, value]) => {
            if (!counters[operatorKey]) {
              return;
            }
            if (connectionCityKey === 'spb') {
              counters[operatorKey].forSaleSpbSum += value;
            } else {
              counters[operatorKey].forSaleMskSum += value;
            }
          });
        }
      });
    } else {
      const connReserve = {};
      activeConnections.forEach((c) => {
        if (c && c.id != null) {
          connReserve[c.id] = Number(c.reserve_modems_count) || 0;
        }
      });

      const operatorsMap = dashboardStats?.operators && typeof dashboardStats.operators === 'object'
        ? dashboardStats.operators
        : {};
      Object.entries(operatorsMap).forEach(([operatorName, count]) => {
        const operatorKey = mapOperatorNameToKey(operatorName);
        if (!operatorKey || !counters[operatorKey]) return;
        counters[operatorKey].total += Number(count || 0);
      });

      const freeByCity = dashboardStats?.free_operators_by_city && typeof dashboardStats.free_operators_by_city === 'object'
        ? dashboardStats.free_operators_by_city
        : {};
      Object.entries(freeByCity).forEach(([cityName, operatorsByCity]) => {
        const cityKey = mapCityToKey(cityName);
        if (!cityKey || !operatorsByCity || typeof operatorsByCity !== 'object') return;
        Object.entries(operatorsByCity).forEach(([operatorName, count]) => {
          const operatorKey = mapOperatorNameToKey(operatorName);
          if (!operatorKey || !counters[operatorKey]) return;
          if (cityKey === 'spb') counters[operatorKey].spbFree += Number(count || 0);
          if (cityKey === 'msk') counters[operatorKey].mskFree += Number(count || 0);
        });
      });

      const totalModemsAll = operatorOrder.reduce((s, { key }) => s + (counters[key].total || 0), 0);
      const totalReserveAll = Object.values(connReserve).reduce((s, v) => s + (Number(v) || 0), 0);
      operatorOrder.forEach(({ key }) => {
        const t = counters[key].total || 0;
        if (totalModemsAll > 0 && totalReserveAll > 0 && t > 0) {
          counters[key].reserveSum = Math.round((totalReserveAll * t) / totalModemsAll);
        }
        counters[key].freeSum = (counters[key].spbFree || 0) + (counters[key].mskFree || 0);
      });

      operatorOrder.forEach(({ key }) => {
        const t = counters[key].total || 0;
        const r = counters[key].reserveSum || 0;
        const spbT = counters[key].spbTotal || 0;
        const mskT = counters[key].mskTotal || 0;
        if (t > 0 && r > 0) {
          counters[key].reserveSpbSum = spbT > 0 ? Math.round((r * spbT) / t) : 0;
          counters[key].reserveMskSum = mskT > 0 ? Math.round((r * mskT) / t) : 0;
        } else {
          counters[key].reserveSpbSum = 0;
          counters[key].reserveMskSum = 0;
        }
        counters[key].forSaleSum = Math.max(0, (counters[key].freeSum || 0) - (counters[key].reserveSum || 0));
        counters[key].forSaleSpbSum = Math.max(0, (counters[key].spbFree || 0) - (counters[key].reserveSpbSum || 0));
        counters[key].forSaleMskSum = Math.max(0, (counters[key].mskFree || 0) - (counters[key].reserveMskSum || 0));
      });
    }

    return operatorOrder.map(({ key, label }) => {
      const c = counters[key];
      return [
        label,
        c.total,
        c.forSaleSum,
        c.spbTotal,
        c.spbFree,
        c.forSaleSpbSum,
        c.mskTotal,
        c.mskFree,
        c.forSaleMskSum,
      ];
    });
  };

  const mapUnavailableModemStatusLabel = (raw) => {
    const st = String(raw || '').toLowerCase();
    if (st === 'not_working') return 'Недоступен';
    if (st === 'unknown') return 'Неизвестно';
    return raw ? String(raw) : 'Недоступен';
  };

  // Маппинг unavailable_modems_list к формату UnavailableModemsBlock
  const mapUnavailableModems = (modemsList, portsStats) => {
    if (!modemsList || !Array.isArray(modemsList)) {
      return [];
    }
    const loadByModem = {};
    if (Array.isArray(portsStats)) {
      portsStats.forEach((p) => {
        const serverId = p.server_id ?? p.kraken_server_id ?? p.modem_id;
        if (serverId != null) {
          const load = p.overall_load_percentage ?? p.utilization ?? 0;
          const prev = loadByModem[serverId] ?? 0;
          if (load > prev) loadByModem[serverId] = load;
        }
      });
    }
    return modemsList.map((modem, index) => {
      const modemId = modem.modem_id || modem.id;
      const loadPct = modem.overall_load_percentage ?? loadByModem[modemId] ?? 0;
      let loadType = 'Min';
      if (loadPct < 0.1) loadType = 'Free';
      else if (loadPct > 70) loadType = 'Max';

      const ip = modem.ip_address || '';
      const ipParts = ip.split('.');
      const ipNumber = ipParts.length === 4 ? ipParts[2] : null;
      const rawName = modem.modem_name || modem.name || '—';
      const cleanedName = String(rawName).replace(/^№\s*\d+\s*/i, '').trim();
      const modemNumber = ipNumber || modem.kraken_id || modemId;
      const displayName = modemNumber
        ? `№ ${modemNumber} ${cleanedName || '—'}`
        : (cleanedName || '—');

      return {
        id: String(index + 1).padStart(3, '0'),
        modem_id: modemId,
        connection_id: modem.connection_id || modem.kraken_connection_id || null,
        modem_type: modem.modem_type || cleanedName || '',
        server: modem.connection_name || modem.server_name || '—',
        geo: modem.city || '—',
        modemName: displayName,
        status: mapUnavailableModemStatusLabel(modem.status),
        loadType,
        loadPercentage: Math.round(loadPct),
        proxyCount: modem.total_proxies || 0,
        signalLevel: modem.signal_level ?? 0,
        internetStatus: modem.internet_status || 'Отключен',
        operator: normalizeOperator(modem.operator),
        performance: modem.performance != null ? `${modem.performance} Мб/с` : '—',
        reconnectType: modem.reconnect_type || '',
        reconnectInterval: modem.reconnect_interval || 0,
        reconnectTime: modem.reconnect_interval ? `${modem.reconnect_interval} сек` : '—',
        connectionStatus: modem.reconnect_type || '',
        lastConnection: modem.last_connection || '—',
      };
    });
  };

  const handleRestartKraken = useCallback((server) => {
    Modal.confirm({
      title: 'Перезагрузка Kraken',
      content: `Вы уверены, что хотите перезагрузить Kraken на сервере "${server.name}"?`,
      okText: 'Да',
      cancelText: 'Отмена',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await apiService.restartKraken(server.id);
          message.info(`Перезагрузка Kraken на "${server.name}" запущена. Обычно до 2 минут.`);
          fetchData();
        } catch (error) {
          message.error('Ошибка запуска перезагрузки Kraken: ' + (error.response?.data?.detail || error.message));
        }
      }
    });
  }, [fetchData]);

  const handleRebootServer = useCallback((server) => {
    Modal.confirm({
      title: 'Перезагрузка сервера',
      content: `Вы уверены, что хотите перезагрузить сервер "${server.name}"? Это может занять несколько минут.`,
      okText: 'Да, перезагрузить',
      cancelText: 'Отмена',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await apiService.rebootKrakenServer(server.id);
          message.success('Сервер перезагружается...');
          fetchData();
        } catch (error) {
          message.error('Ошибка перезагрузки сервера: ' + (error.response?.data?.detail || error.message));
        }
      }
    });
  }, [fetchData]);

  const fetchDashboardModemRebootStates = useCallback(async (modemsList = []) => {
    const modemIds = Array.from(
      new Set(
        (Array.isArray(modemsList) ? modemsList : [])
          .map((item) => Number(item?.modem_id || 0))
          .filter((value) => Number.isFinite(value) && value > 0)
      )
    );
    if (modemIds.length === 0) {
      setModemRebootStateById({});
      return;
    }
    try {
      const response = await apiService.getModemRebootStatus({ modem_ids: modemIds });
      setModemRebootStateById(buildModemRebootStateMap(response?.data?.data || response?.data || {}));
    } catch (error) {
      console.warn('Не удалось загрузить состояния reboot модемов для dashboard:', error);
    }
  }, []);

  // Формат числа для data-блоков
  const formatNum = (num) => {
    if (num === null || num === undefined) return 0;
    return num;
  };

  /**
   * Порты с живым ответом Kraken для подключения. При недоступном Kraken порты всё ещё в списке
   * сетевых портов, но не участвуют в статистике «Модемы» на дашборде.
   */
  const unavailableModemsSourceList = useMemo(() => {
    const raw = stats?.unavailable_modems_list;
    return Array.isArray(raw) ? raw : [];
  }, [stats?.unavailable_modems_list]);

  /**
   * Всего модемов — сумма servers_count по подключениям (как API /api/kraken-connections).
   */
  const dashboardModemTotalFromConnections = useMemo(() => {
    if (!Array.isArray(connections) || connections.length === 0) {
      return null;
    }
    return connections
      .filter((conn) => conn && conn.is_active !== false)
      .reduce(
        (sum, conn) => sum + Number(conn.servers_count ?? conn.modems_count ?? 0),
        0
      );
  }, [connections]);

  const dashboardModemUnavailableFromConnections = useMemo(() => {
    if (!Array.isArray(connections) || connections.length === 0) {
      return null;
    }
    return connections
      .filter((conn) => conn && conn.is_active !== false)
      .reduce((sum, conn) => sum + Number(conn.unavailable_modems ?? 0), 0);
  }, [connections]);

  /**
   * Круговая диаграмма «Модемы»: total/unavailable — из API подключений (servers_count).
   */
  const dashboardModemDonutStats = useMemo(() => {
    if (dashboardModemTotalFromConnections == null) {
      return null;
    }
    const total = dashboardModemTotalFromConnections;
    const unavailable = Math.min(dashboardModemUnavailableFromConnections ?? 0, total);
    const available = Math.max(0, total - unavailable);
    return { total, available, unavailable };
  }, [dashboardModemTotalFromConnections, dashboardModemUnavailableFromConnections]);

  const stableDashboardModemDonutStats = useMemo(() => {
    if (dashboardModemDonutStats) {
      lastDashboardModemDonutStatsRef.current = dashboardModemDonutStats;
      return dashboardModemDonutStats;
    }
    return lastDashboardModemDonutStatsRef.current;
  }, [dashboardModemDonutStats]);

  const modemBlockStats = stableDashboardModemDonutStats || (
    connectionsLoading
      ? null
      : {
          total: stats?.total_modems ?? 0,
          available: stats?.available_modems ?? 0,
          unavailable: stats?.unavailable_modems ?? 0,
        }
  );

  const refreshDashboardSnapshot = useCallback(async (scope) => {
    const params = { scope };
    if (scope === 'sales') {
      params.date_from = formatSalesApiDate(salesFilters.rangeStart);
      params.date_to = formatSalesApiDate(salesFilters.rangeEnd);
      params.geo = salesFilters.geoFilter;
      params.period = salesFilters.periodFilter;
      params.chart_granularity = autoChartGranularity;
    }
    await apiService.refreshDashboardCache(params);
  }, [
    autoChartGranularity,
    salesFilters.geoFilter,
    salesFilters.periodFilter,
    salesFilters.rangeEnd,
    salesFilters.rangeStart,
  ]);

  const handleRefreshClick = useCallback(async () => {
    if (manualRefreshInFlight) {
      return;
    }
    setManualRefreshInFlight(true);
    try {
      if (activeDashboardTab === 'sales') {
        await refreshDashboardSnapshot('sales');
        await fetchSalesData();
        return;
      }
      await refreshDashboardSnapshot('system');
      await fetchData(false, { replaceStats: true });
    } catch (error) {
      message.error(
        error?.response?.data?.detail || error?.message || 'Не удалось обновить данные дашборда'
      );
    } finally {
      setManualRefreshInFlight(false);
    }
  }, [
    activeDashboardTab,
    fetchData,
    fetchSalesData,
    manualRefreshInFlight,
    refreshDashboardSnapshot,
  ]);

  const isRefreshLoading = manualRefreshInFlight
    || (activeDashboardTab === 'sales' ? salesLoading : loading);

  const refreshActionButton = (
    <button
      type="button"
      className={`dashboard-header-row__refresh-btn dashboard-header-row__icon-btn dashboard-header-row__icon-btn--primary${isRefreshLoading ? ' dashboard-header-row__icon-btn--spinning' : ''}`}
      onClick={handleRefreshClick}
      title="Обновить данные"
      aria-label="Обновить"
      disabled={isRefreshLoading}
      tabIndex={0}
    >
      <svg
        className="dashboard-header-row__refresh-icon dashboard-header-row__icon-btn-svg"
        viewBox="0 0 12 12"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <g clipPath="url(#dashboard-refresh-clip)">
          <path d="M6.0001 1.00014C6.66336 1.00231 7.31959 1.13601 7.93084 1.39348C8.54208 1.65096 9.09619 2.0271 9.5611 2.50014H8.0001C7.86749 2.50014 7.74032 2.55282 7.64655 2.64658C7.55278 2.74035 7.5001 2.86753 7.5001 3.00014C7.5001 3.13275 7.55278 3.25992 7.64655 3.35369C7.74032 3.44746 7.86749 3.50014 8.0001 3.50014H10.0716C10.3178 3.50001 10.5539 3.40214 10.728 3.22804C10.9021 3.05394 11 2.81785 11.0001 2.57164V0.500138C11.0001 0.36753 10.9474 0.240353 10.8537 0.146585C10.7599 0.0528164 10.6327 0.000137982 10.5001 0.000137982C10.3675 0.000137982 10.2403 0.0528164 10.1465 0.146585C10.0528 0.240353 10.0001 0.36753 10.0001 0.500138V1.53914C9.17369 0.794864 8.15639 0.295343 7.06215 0.0965125C5.96791 -0.102318 4.83991 0.00739396 3.80449 0.413362C2.76907 0.81933 1.86711 1.50552 1.19961 2.3951C0.532105 3.28468 0.125422 4.34251 0.0251013 5.45014C0.0186429 5.51977 0.0267591 5.58998 0.0489327 5.6563C0.0711063 5.72262 0.106851 5.78359 0.153889 5.83533C0.200928 5.88707 0.258228 5.92845 0.32214 5.95682C0.386052 5.9852 0.455174 5.99995 0.525101 6.00014C0.647395 6.0017 0.765868 5.95756 0.857331 5.87636C0.948793 5.79516 1.00666 5.68276 1.0196 5.56114C1.13091 4.31651 1.70359 3.15849 2.62514 2.31456C3.54669 1.47062 4.75051 1.00178 6.0001 1.00014Z" fill="currentColor" />
          <path d="M11.4756 6.00004C11.3533 5.99848 11.2349 6.04262 11.1434 6.12382C11.0519 6.20501 10.9941 6.31742 10.9811 6.43904C10.8985 7.39066 10.5446 8.2986 9.96145 9.05515C9.37831 9.8117 8.5904 10.3851 7.6912 10.7073C6.79199 11.0296 5.81922 11.0871 4.8883 10.8731C3.95738 10.6591 3.10737 10.1826 2.43912 9.50005H4.00012C4.13273 9.50005 4.25991 9.44737 4.35368 9.3536C4.44744 9.25983 4.50012 9.13266 4.50012 9.00005C4.50012 8.86744 4.44744 8.74026 4.35368 8.64649C4.25991 8.55272 4.13273 8.50005 4.00012 8.50005H1.92862C1.80667 8.49998 1.6859 8.52395 1.57322 8.57059C1.46054 8.61723 1.35816 8.68562 1.27193 8.77185C1.18569 8.85808 1.1173 8.96047 1.07067 9.07315C1.02403 9.18583 1.00006 9.3066 1.00012 9.42855V11.5001C1.00012 11.6327 1.0528 11.7598 1.14657 11.8536C1.24034 11.9474 1.36751 12.0001 1.50012 12.0001C1.63273 12.0001 1.75991 11.9474 1.85368 11.8536C1.94744 11.7598 2.00012 11.6327 2.00012 11.5001V10.4611C2.82654 11.2053 3.84383 11.7048 4.93807 11.9037C6.03232 12.1025 7.16031 11.9928 8.19573 11.5868C9.23115 11.1809 10.1331 10.4947 10.8006 9.60509C11.4681 8.71551 11.8748 7.65767 11.9751 6.55004C11.9816 6.48041 11.9735 6.4102 11.9513 6.34388C11.9291 6.27756 11.8934 6.21659 11.8463 6.16485C11.7993 6.11311 11.742 6.07173 11.6781 6.04335C11.6142 6.01498 11.545 6.00023 11.4751 6.00004H11.4756Z" fill="currentColor" />
        </g>
        <defs>
          <clipPath id="dashboard-refresh-clip">
            <rect width="12" height="12" fill="white" />
          </clipPath>
        </defs>
      </svg>
    </button>
  );

  const desktopRecheckActionButton = (
    <button
      type="button"
      className={`page-header-block__btn page-header-block__btn--icon page-header-block__btn--recheck-bug${recheckLoading ? ' page-header-block__btn--recheck-bug-loading' : ''}`}
      onClick={handleRecheck}
      disabled={recheckLoading}
      title={recheckLoading ? 'Проверка прокси...' : 'Перепроверить прокси'}
      aria-label={recheckLoading ? 'Идет перепроверка прокси' : 'Перепроверить прокси'}
      tabIndex={0}
    >
      <svg
        className="page-header-block__bug-icon"
        viewBox="0 0 12 12"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <g clipPath="url(#dashboard-bug-clip)">
          <path d="M11 3.5C11.2652 3.5 11.5196 3.39464 11.7071 3.20711C11.8946 3.01957 12 2.76522 12 2.5V0.5C12 0.367392 11.9473 0.240215 11.8536 0.146447C11.7598 0.0526784 11.6326 0 11.5 0C11.3674 0 11.2402 0.0526784 11.1464 0.146447C11.0527 0.240215 11 0.367392 11 0.5V2.5H9C8.991 2.5 8.984 2.5045 8.975 2.505C8.73585 1.9125 8.32509 1.40499 7.79545 1.04761C7.2658 0.690226 6.64144 0.499281 6.0025 0.499281C5.36356 0.499281 4.7392 0.690226 4.20955 1.04761C3.67991 1.40499 3.26915 1.9125 3.03 2.505C3.0195 2.5055 3.0105 2.5 3 2.5H1V0.5C1 0.367392 0.947322 0.240215 0.853553 0.146447C0.759785 0.0526784 0.632608 0 0.5 0C0.367392 0 0.240215 0.0526784 0.146447 0.146447C0.0526784 0.240215 0 0.367392 0 0.5L0 2.5C0 2.76522 0.105357 3.01957 0.292893 3.20711C0.48043 3.39464 0.734784 3.5 1 3.5H2.7425L2.236 5.5H0.5C0.367392 5.5 0.240215 5.55268 0.146447 5.64645C0.0526784 5.74021 0 5.86739 0 6C0 6.13261 0.0526784 6.25979 0.146447 6.35355C0.240215 6.44732 0.367392 6.5 0.5 6.5H1.9825L1.91 6.7865L1.9 6.85C1.87771 7.02111 1.86602 7.19344 1.865 7.366C1.86551 7.74969 1.91987 8.13142 2.0265 8.5H1C0.734784 8.5 0.48043 8.60536 0.292893 8.79289C0.105357 8.98043 0 9.23478 0 9.5L0 11.5C0 11.6326 0.0526784 11.7598 0.146447 11.8536C0.240215 11.9473 0.367392 12 0.5 12C0.632608 12 0.759785 11.9473 0.853553 11.8536C0.947322 11.7598 1 11.6326 1 11.5V9.5H2.465C2.83155 10.1101 3.34976 10.6149 3.96921 10.9654C4.58866 11.3159 5.28827 11.5001 6 11.5001C6.71173 11.5001 7.41134 11.3159 8.03079 10.9654C8.65024 10.6149 9.16845 10.1101 9.535 9.5H11V11.5C11 11.6326 11.0527 11.7598 11.1464 11.8536C11.2402 11.9473 11.3674 12 11.5 12C11.6326 12 11.7598 11.9473 11.8536 11.8536C11.9473 11.7598 12 11.6326 12 11.5V9.5C12 9.23478 11.8946 8.98043 11.7071 8.79289C11.5196 8.60536 11.2652 8.5 11 8.5H9.9735C10.0807 8.13066 10.1354 7.74808 10.136 7.3635C10.1345 7.19176 10.1225 7.02027 10.1 6.85L10.0125 6.5H11.5C11.6326 6.5 11.7598 6.44732 11.8536 6.35355C11.9473 6.25979 12 6.13261 12 6C12 5.86739 11.9473 5.74021 11.8536 5.64645C11.7598 5.55268 11.6326 5.5 11.5 5.5H9.761L9.2565 3.5H11ZM6 10.5C5.16848 10.4991 4.37129 10.1683 3.78336 9.58029C3.19543 8.99227 2.86479 8.19502 2.864 7.3635C2.86469 7.24198 2.87253 7.1206 2.8875 7L3.9025 2.985C4.05483 2.55255 4.33709 2.17775 4.71065 1.91191C5.0842 1.64606 5.53079 1.50217 5.98928 1.49992C6.44777 1.49768 6.89575 1.63719 7.27189 1.89937C7.64803 2.16154 7.93394 2.53356 8.0905 2.9645L9.1125 7C9.12743 7.12027 9.13527 7.24131 9.136 7.3625C9.13547 8.19419 8.80495 8.9917 8.21699 9.57993C7.62904 10.1682 6.83169 10.4991 6 10.5Z" fill="currentColor"/>
        </g>
        <defs>
          <clipPath id="dashboard-bug-clip">
            <rect width="12" height="12" fill="white"/>
          </clipPath>
        </defs>
      </svg>
    </button>
  );

  const mobileRecheckActionButton = (
    <button
      type="button"
      className={`dashboard-header-row__icon-btn dashboard-header-row__icon-btn--muted${recheckLoading ? ' dashboard-header-row__icon-btn--loading' : ''}`}
      onClick={handleRecheck}
      disabled={recheckLoading}
      title={recheckLoading ? 'Проверка прокси...' : 'Перепроверить прокси'}
      aria-label={recheckLoading ? 'Идет перепроверка прокси' : 'Перепроверить прокси'}
      tabIndex={0}
    >
      <svg
        className="dashboard-header-row__icon-btn-svg"
        viewBox="0 0 12 12"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <g clipPath="url(#dashboard-mobile-bug-clip)">
          <path d="M11 3.5C11.2652 3.5 11.5196 3.39464 11.7071 3.20711C11.8946 3.01957 12 2.76522 12 2.5V0.5C12 0.367392 11.9473 0.240215 11.8536 0.146447C11.7598 0.0526784 11.6326 0 11.5 0C11.3674 0 11.2402 0.0526784 11.1464 0.146447C11.0527 0.240215 11 0.367392 11 0.5V2.5H9C8.991 2.5 8.984 2.5045 8.975 2.505C8.73585 1.9125 8.32509 1.40499 7.79545 1.04761C7.2658 0.690226 6.64144 0.499281 6.0025 0.499281C5.36356 0.499281 4.7392 0.690226 4.20955 1.04761C3.67991 1.40499 3.26915 1.9125 3.03 2.505C3.0195 2.5055 3.0105 2.5 3 2.5H1V0.5C1 0.367392 0.947322 0.240215 0.853553 0.146447C0.759785 0.0526784 0.632608 0 0.5 0C0.367392 0 0.240215 0.0526784 0.146447 0.146447C0.0526784 0.240215 0 0.367392 0 0.5L0 2.5C0 2.76522 0.105357 3.01957 0.292893 3.20711C0.48043 3.39464 0.734784 3.5 1 3.5H2.7425L2.236 5.5H0.5C0.367392 5.5 0.240215 5.55268 0.146447 5.64645C0.0526784 5.74021 0 5.86739 0 6C0 6.13261 0.0526784 6.25979 0.146447 6.35355C0.240215 6.44732 0.367392 6.5 0.5 6.5H1.9825L1.91 6.7865L1.9 6.85C1.87771 7.02111 1.86602 7.19344 1.865 7.366C1.86551 7.74969 1.91987 8.13142 2.0265 8.5H1C0.734784 8.5 0.48043 8.60536 0.292893 8.79289C0.105357 8.98043 0 9.23478 0 9.5L0 11.5C0 11.6326 0.0526784 11.7598 0.146447 11.8536C0.240215 11.9473 0.367392 12 0.5 12C0.632608 12 0.759785 11.9473 0.853553 11.8536C0.947322 11.7598 1 11.6326 1 11.5V9.5H2.465C2.83155 10.1101 3.34976 10.6149 3.96921 10.9654C4.58866 11.3159 5.28827 11.5001 6 11.5001C6.71173 11.5001 7.41134 11.3159 8.03079 10.9654C8.65024 10.6149 9.16845 10.1101 9.535 9.5H11V11.5C11 11.6326 11.0527 11.7598 11.1464 11.8536C11.2402 11.9473 11.3674 12 11.5 12C11.6326 12 11.7598 11.9473 11.8536 11.8536C11.9473 11.7598 12 11.6326 12 11.5V9.5C12 9.23478 11.8946 8.98043 11.7071 8.79289C11.5196 8.60536 11.2652 8.5 11 8.5H9.9735C10.0807 8.13066 10.1354 7.74808 10.136 7.3635C10.1345 7.19176 10.1225 7.02027 10.1 6.85L10.0125 6.5H11.5C11.6326 6.5 11.7598 6.44732 11.8536 6.35355C11.9473 6.25979 12 6.13261 12 6C12 5.86739 11.9473 5.74021 11.8536 5.64645C11.7598 5.55268 11.6326 5.5 11.5 5.5H9.761L9.2565 3.5H11ZM6 10.5C5.16848 10.4991 4.37129 10.1683 3.78336 9.58029C3.19543 8.99227 2.86479 8.19502 2.864 7.3635C2.86469 7.24198 2.87253 7.1206 2.8875 7L3.9025 2.985C4.05483 2.55255 4.33709 2.17775 4.71065 1.91191C5.0842 1.64606 5.53079 1.50217 5.98928 1.49992C6.44777 1.49768 6.89575 1.63719 7.27189 1.89937C7.64803 2.16154 7.93394 2.53356 8.0905 2.9645L9.1125 7C9.12743 7.12027 9.13527 7.24131 9.136 7.3625C9.13547 8.19419 8.80495 8.9917 8.21699 9.57993C7.62904 10.1682 6.83169 10.4991 6 10.5Z" fill="currentColor"/>
        </g>
        <defs>
          <clipPath id="dashboard-mobile-bug-clip">
            <rect width="12" height="12" fill="white"/>
          </clipPath>
        </defs>
      </svg>
    </button>
  );

  const mobileFilterActionButton = (
    <button
      type="button"
      className={`dashboard-header-row__icon-btn dashboard-header-row__icon-btn--muted${isSalesMobileFiltersOpen ? ' dashboard-header-row__icon-btn--active' : ''}`}
      onClick={handleToggleSalesMobileFilters}
      onKeyDown={handleToggleSalesMobileFiltersKeyDown}
      title="Фильтры"
      aria-label="Фильтры"
      aria-pressed={isSalesMobileFiltersOpen}
      tabIndex={0}
    >
      <svg
        className="dashboard-header-row__icon-btn-svg"
        viewBox="0 0 12 12"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <g clipPath="url(#dashboard-filter-clip)">
          <path d="M6.99999 12C6.89181 12 6.78654 11.9649 6.69999 11.9L4.69999 10.4C4.63789 10.3534 4.58749 10.293 4.55278 10.2236C4.51806 10.1542 4.49999 10.0776 4.49999 10V7.19L0.99199 3.2435C0.74285 2.96244 0.58018 2.61541 0.52353 2.24412C0.466881 1.87283 0.518662 1.49308 0.672652 1.15051C0.826641 0.807938 1.07629 0.517129 1.39158 0.313033C1.70688 0.108936 2.0744 0.000237209 2.44999 0L9.54999 0C9.92554 0.000440525 10.293 0.10932 10.6081 0.313553C10.9233 0.517787 11.1728 0.808686 11.3266 1.15129C11.4805 1.4939 11.5321 1.87364 11.4753 2.24487C11.4185 2.61611 11.2557 2.96305 11.0065 3.244L7.49999 7.19V11.5C7.49999 11.6326 7.44731 11.7598 7.35354 11.8536C7.25978 11.9473 7.1326 12 6.99999 12ZM5.49999 9.75L6.49999 10.5V7C6.50009 6.87758 6.5451 6.75945 6.62649 6.668L10.2605 2.5795C10.3817 2.44254 10.4607 2.27352 10.4882 2.09273C10.5157 1.91193 10.4904 1.72705 10.4154 1.56028C10.3404 1.39351 10.2188 1.25193 10.0653 1.15254C9.91179 1.05316 9.73286 1.00019 9.54999 1H2.44999C2.26722 1.00028 2.0884 1.05327 1.93499 1.15263C1.78158 1.25199 1.66009 1.3935 1.58509 1.56018C1.51009 1.72685 1.48477 1.91163 1.51215 2.09234C1.53954 2.27305 1.61847 2.44203 1.73949 2.579L5.37399 6.668C5.4552 6.75952 5.50003 6.87764 5.49999 7V9.75Z" fill="currentColor"/>
        </g>
        <defs>
          <clipPath id="dashboard-filter-clip">
            <rect width="12" height="12" fill="white"/>
          </clipPath>
        </defs>
      </svg>
    </button>
  );

  const renderSalesDateRange = (className = '') => (
    <DashboardSalesDateRange
      dateRangeLabel={salesFilters.dateRangeLabel}
      rangeStart={salesFilters.rangeStart}
      rangeEnd={salesFilters.rangeEnd}
      periodFilter={salesFilters.periodFilter}
      canGoNext={salesFilters.canGoNext}
      onPrevRange={salesFilters.handlePrevRange}
      onNextRange={salesFilters.handleNextRange}
      onCustomRangeChange={salesFilters.handleCustomRangeChange}
      onPeriodFilterChange={salesFilters.handlePeriodFilterChange}
      className={className}
    />
  );

  const unavailablePortsSource = stats?.ports_stats;
  const unavailableModems = mapUnavailableModems(unavailableModemsSourceList, unavailablePortsSource);
  const unavailableModemIdsKey = useMemo(
    () => unavailableModems
      .map((item) => Number(item?.modem_id || 0))
      .filter((value) => Number.isFinite(value) && value > 0)
      .sort((a, b) => a - b)
      .join(','),
    [unavailableModems],
  );
  const lastDashboardRebootFetchRef = React.useRef({ key: '', at: 0 });
  useEffect(() => {
    if (!unavailableModemIdsKey) {
      return undefined;
    }
    const now = Date.now();
    const prev = lastDashboardRebootFetchRef.current;
    if (prev.key === unavailableModemIdsKey && now - prev.at < 20000) {
      return undefined;
    }
    lastDashboardRebootFetchRef.current = { key: unavailableModemIdsKey, at: now };
    void fetchDashboardModemRebootStates(unavailableModems);
    return undefined;
  }, [fetchDashboardModemRebootStates, unavailableModemIdsKey, unavailableModems]);

  const isDashboardModemRebootDisabled = useCallback((modem) => {
    const modemId = Number(modem?.modem_id || 0);
    if (!modemId) {
      return true;
    }
    if (modem?.is_active === false || modem?.active === false) {
      return true;
    }
    if (!isSupportedModemRebootModel(resolveModemRebootModelName(modem))) {
      return true;
    }
    const rebootState = modemRebootStateById[modemId];
    if (rebootState?.last_result === 'in_progress') {
      return true;
    }
    return isRebootCooldownActive(rebootState);
  }, [modemRebootStateById]);

  const handleRebootUnavailableModem = useCallback(async (modem) => {
    const modemId = Number(modem?.modem_id || 0);
    if (!modemId) {
      message.error('Не удалось определить модем для перезагрузки.');
      return;
    }
    if (isDashboardModemRebootDisabled(modem)) {
      message.warning('Для модема активен cooldown, reboot уже идет или модель не поддерживается.');
      return;
    }
    try {
      await apiService.rebootModem(modemId);
      message.success(`Перезагрузка модема ${modem.modemName || `#${modemId}`} запущена.`);
      await fetchData();
      await fetchDashboardModemRebootStates(unavailableModems);
    } catch (error) {
      message.error('Ошибка перезагрузки модема: ' + (error.response?.data?.detail || error.message));
    }
  }, [fetchDashboardModemRebootStates, fetchData, isDashboardModemRebootDisabled, unavailableModems]);

  const handleRebootAllUnavailableModems = useCallback(async () => {
    setDashboardModemRebootLoading(true);
    try {
      const response = await apiService.rebootDashboardUnavailableModems();
      const payload = response?.data?.data || {};
      const acceptedCount = Array.isArray(payload.accepted) ? payload.accepted.length : 0;
      const skippedCount = Array.isArray(payload.skipped) ? payload.skipped.length : 0;
      message.success(`Перезагрузка недоступных модемов запущена: принято ${acceptedCount}, пропущено ${skippedCount}.`);
      await fetchData();
      await fetchDashboardModemRebootStates(unavailableModems);
    } catch (error) {
      message.error('Ошибка запуска массовой перезагрузки модемов: ' + (error.response?.data?.detail || error.message));
    } finally {
      setDashboardModemRebootLoading(false);
    }
  }, [fetchDashboardModemRebootStates, fetchData, unavailableModems]);

  const renderDashboardHeader = () => (
    <div className="dashboard-header-row">
      <div className="dashboard-header-row__bar">
        <h1 className="dashboard-header-row__title">Дашборд</h1>
        {activeDashboardTab === 'sales' && isMobileViewport ? renderSalesDateRange() : null}
        <div className="dashboard-header-row__icon-actions">
          {activeDashboardTab === 'sales' ? mobileFilterActionButton : mobileRecheckActionButton}
          {refreshActionButton}
        </div>
      </div>
      <div className="dashboard-header-row__sub">
        <DashboardTabs
          activeTab={activeDashboardTab}
          onTabChange={handleDashboardTabChange}
          variant="header"
        />
        {activeDashboardTab === 'sales' ? (
          <DashboardSalesToolbar
            geoFilter={salesFilters.geoFilter}
            periodFilter={salesFilters.periodFilter}
            onGeoFilterChange={salesFilters.setGeoFilter}
            onPeriodFilterChange={salesFilters.handlePeriodFilterChange}
            onRefresh={handleRefreshClick}
            refreshing={salesLoading || manualRefreshInFlight}
            dateRangeControl={renderSalesDateRange('dashboard-header-row__date-range--in-toolbar')}
          />
        ) : (
          <div className="dashboard-header-row__actions dashboard-header-row__actions--desktop">
            <div className="dashboard-header-row__system-actions">
              <button
                type="button"
                className={`page-header-block__btn page-header-block__btn--icon page-header-block__btn--recheck-bug${dashboardModemRebootLoading ? ' page-header-block__btn--recheck-bug-loading' : ''}`}
                onClick={handleRebootAllUnavailableModems}
                disabled={dashboardModemRebootLoading || unavailableModems.length === 0}
                title={dashboardModemRebootLoading ? 'Перезагрузка модемов...' : 'Перезагрузить недоступные модемы'}
                aria-label={dashboardModemRebootLoading ? 'Идет перезагрузка недоступных модемов' : 'Перезагрузить недоступные модемы'}
                tabIndex={0}
              >
                <svg
                  className="page-header-block__bug-icon"
                  viewBox="0 0 12 12"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <path d="M10 5.5A4.5 4.5 0 105.5 10" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M10 2V5.5H6.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
              {desktopRecheckActionButton}
              {refreshActionButton}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="dashboard-page">
        {renderDashboardHeader()}
        <div className="dashboard-loading">
          <div className="loader-container">
            <div className="loader-ball" />
            <div className="loader-shadow" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-page">
        {renderDashboardHeader()}
        <div className="dashboard-error">
          <p>Ошибка: {error}</p>
          <button onClick={fetchData} className="dashboard-retry-btn">
            Повторить
          </button>
        </div>
      </div>
    );
  }

  const servers = mergeKrakenHostResources(
    mapConnections(connections, []),
    krakenHostStats
  );

  // Данные для SIM-КАРТЫ таблицы (по макету Figma)
  const simTotal = simStats?.total || 0;
  const byGeo = simStats?.by_geo || {};
  const simSpbCount = byGeo['СПб'] || byGeo['spb'] || byGeo['SPB'] || 0;
  const simMskCount = byGeo['Мск'] || byGeo['Москва'] || byGeo['msk'] || byGeo['MSK'] || 0;
  const reserveSpb = simStats?.reserves?.spb || 0;
  const reserveMsk = simStats?.reserves?.msk || 0;
  const reserveTotal = reserveSpb + reserveMsk;
  const frozenByGeo = simStats?.frozen_by_geo || {};
  const frozenSpb = frozenByGeo['СПб'] || frozenByGeo['spb'] || frozenByGeo['SPB'] || 0;
  const frozenMsk = frozenByGeo['Мск'] || frozenByGeo['Москва'] || frozenByGeo['msk'] || frozenByGeo['MSK'] || 0;
  const frozenTotal = Number(simStats?.frozen_total ?? 0);
  const operatorsSummaryRows = (operatorWarehouseStock.rows || [])
    .map((row) => {
      const label = String(row?.operator || '').trim();
      if (!label) return null;
      return [
        label,
        Number(row.count_spb || 0),
        Number(row.count_msk || 0),
        Number(row.common_spb || 0),
        Number(row.common_plus_spb || 0),
        Number(row.individual_spb || 0),
        Number(row.common_msk || 0),
        Number(row.common_plus_msk || 0),
        Number(row.individual_msk || 0),
      ];
    })
    .filter((row) => {
      if (!Array.isArray(row) || row.length < 2) return false;
      return row.slice(1).some((value) => Number(value || 0) > 0);
    });

  const operatorsSummaryTotalRow = (() => {
    if (operatorsSummaryRows.length === 0) return null;
    const apiTotals = operatorWarehouseStock.totals;
    const countSpb = apiTotals && apiTotals.count_spb != null
      ? Number(apiTotals.count_spb || 0)
      : operatorsSummaryRows.reduce((s, row) => s + Number(row[1] || 0), 0);
    const countMsk = apiTotals && apiTotals.count_msk != null
      ? Number(apiTotals.count_msk || 0)
      : operatorsSummaryRows.reduce((s, row) => s + Number(row[2] || 0), 0);
    if (apiTotals && typeof apiTotals === 'object') {
      return [
        'Итого',
        countSpb,
        countMsk,
        Number(apiTotals.common_spb || 0),
        Number(apiTotals.common_plus_spb || 0),
        Number(apiTotals.individual_spb || 0),
        Number(apiTotals.common_msk || 0),
        Number(apiTotals.common_plus_msk || 0),
        Number(apiTotals.individual_msk || 0),
      ];
    }
    const totals = [0, 0, 0, 0, 0, 0, 0, 0];
    operatorsSummaryRows.forEach((row) => {
      for (let c = 0; c < 8; c += 1) {
        totals[c] += Number(row[c + 1] || 0);
      }
    });
    return ['Итого', ...totals];
  })();

  const sortedOperatorsSummaryRows = (() => {
    let dataRows = operatorsSummaryRows;
    if (operatorTableSort.columnIndex !== null) {
      const directionFactor = operatorTableSort.direction === 'asc' ? 1 : -1;
      dataRows = [...operatorsSummaryRows].sort((a, b) => {
        const idx = operatorTableSort.columnIndex;
        if (idx === 0) {
          return String(a[0] || '').localeCompare(String(b[0] || ''), 'ru') * directionFactor;
        }
        const aVal = Number(a[idx] || 0);
        const bVal = Number(b[idx] || 0);
        if (aVal !== bVal) {
          return (aVal - bVal) * directionFactor;
        }
        return String(a[0] || '').localeCompare(String(b[0] || ''), 'ru');
      });
    }
    return operatorsSummaryTotalRow ? [...dataRows, operatorsSummaryTotalRow] : dataRows;
  })();

  const handleOperatorTableSort = (columnIndex) => {
    setOperatorTableSort((prev) => {
      if (prev.columnIndex !== columnIndex) {
        const initialDirection = columnIndex === 0 ? 'asc' : 'desc';
        return { columnIndex, direction: initialDirection };
      }
      return {
        columnIndex,
        direction: prev.direction === 'desc' ? 'asc' : 'desc',
      };
    });
  };

  // Данные для ТЕКУЩИЕ ОПЛАТЫ СИМ
  const upcoming = simStats?.upcoming_payments || {};
  const urgencySums = simStats?.urgency_sums || { red: 0, blue: 0, black: 0, total_3days: 0 };

  const renderReconnectType = (value) => {
    if (!value) return '—';
    if (value === 'time_link') return 'Время + ссылка';
    if (value === 'time') return 'Время';
    if (value === 'link') return 'Ссылка';
    return value;
  };

  const renderIntervalSeconds = (reconnectType, reconnectInterval) => {
    if (!reconnectType || reconnectType === 'link') return '—';
    if (reconnectInterval == null) return '—';
    const interval = Number(reconnectInterval);
    if (!Number.isFinite(interval) || interval <= 0) return '—';
    return `${interval}s`;
  };

  const buildModemDisplayName = (row) => {
    const modemName = String(row?.modem_name || '').trim();
    const modemIp = String(row?.modem_ip_address || '').trim();
    const ipParts = modemIp.split('.');
    const thirdOctet = ipParts.length === 4 ? ipParts[2] : '';
    if (thirdOctet && modemName) return `№ ${thirdOctet} ${modemName}`;
    if (modemName) return modemName;
    return '—';
  };

  const renderMismatchStatus = (row) => {
    const reasons = Array.isArray(row?.mismatch_reasons) ? row.mismatch_reasons : [];
    if (reasons.length > 0) {
      const labels = reasons.map((reason) => {
        if (reason === 'os_fingerprint') return 'OSFP';
        if (reason === 'reconnect_type') return 'Ротация';
        if (reason === 'reconnect_interval') return 'Интервал';
        if (reason === 'reconnect_min') return 'Мин. шаг';
        if (reason === 'monitoring_interval') return 'Мониторинг';
        if (reason === 'mtu') return 'MTU';
        return 'Расхождение';
      });
      return Array.from(new Set(labels)).join(', ');
    }
    return 'Расхождение';
  };

  const dashboardPageClassName = [
    'dashboard-page',
    'pm-content-fade-in',
    activeDashboardTab === 'sales' ? 'dashboard-page--sales' : '',
  ].filter(Boolean).join(' ');

  return (
    <div className={dashboardPageClassName}>
      {renderDashboardHeader()}
      {activeDashboardTab === 'sales' && isSalesMobileFiltersOpen ? (
        <DashboardSalesMobileFilterSheet
          isOpen={isSalesMobileFiltersOpen}
          geoFilter={salesFilters.geoFilter}
          periodFilter={salesFilters.periodFilter}
          visibleDataSections={visibleSalesDataSections}
          onClose={handleCloseSalesMobileFilters}
          onApply={handleSalesMobileFiltersApply}
          onReset={handleSalesMobileFiltersReset}
        />
      ) : null}
      {proxyCheckSummary ? (
        <div className="dashboard-page__check-banner" aria-live="polite">
          <CheckResultBanner
            text={proxyCheckSummary.text}
            tone={proxyCheckSummary.tone}
            onClose={() => setProxyCheckSummary(null)}
          />
        </div>
      ) : null}

      {activeDashboardTab === 'system' ? (
        <div
          id="dashboard-panel-system"
          className="dashboard-tab-panel"
          role="tabpanel"
          aria-labelledby="dashboard-tab-system"
        >
          <div className="dashboard-page__stat-row">
            {/* Ряд 1 — 3 статистических блока с круговыми диаграммами */}
            <ProxyBlock
              title="прокси"
              total={stats?.total_proxies || 0}
              available={stats?.available_proxies || 0}
              unavailable={stats?.unavailable_proxies || 0}
              labelAvailable="Доступно"
              labelUnavailable="Недоступно"
              linkTo="/proxies?availability=unavailable"
              linkAriaLabel="Прокси: открыть список недоступных"
            />
            <ProxyBlock
              title="модемы"
              total={modemBlockStats?.total ?? 0}
              available={modemBlockStats?.available ?? 0}
              unavailable={modemBlockStats?.unavailable ?? 0}
              labelAvailable="Доступно"
              labelUnavailable="Недоступно"
              linkTo="/network-ports"
            />
            <ProxyBlock
              title="сводные модемы"
              total={stats?.free_modems || 0}
              available={stats?.free_modems_spb || 0}
              unavailable={stats?.free_modems_moscow || 0}
              labelAvailable="СПб"
              labelUnavailable="Мск"
              linkTo="/network-ports"
            />
          </div>

          <ServerSummaryBlock
            title="Сводка по серверам"
            linkTo="/kraken-multi"
            servers={servers}
            loading={connectionsLoading}
            onRestartKraken={handleRestartKraken}
            onRebootServer={handleRebootServer}
          />

          <DashboardDataBlock
            className="ddb--full"
            title="Сводка по операторам"
            linkTo="/network-ports"
            columns={[
              'Оператор',
              'Кол-во СПб',
              'Кол-во Мск',
              'Общий СПб',
              'Общий+ СПб',
              'Индив СПб',
              'Общий МСК',
              'Общий+ МСК',
              'Индив МСК',
            ]}
            rows={sortedOperatorsSummaryRows}
            hasTotalRow={!!operatorsSummaryTotalRow}
            sortableColumns={[0, 1, 2, 3, 4, 5, 6, 7, 8]}
            sortState={operatorTableSort}
            onSortChange={handleOperatorTableSort}
          />

          <DashboardSimSummaryBlock
            summary={
              simStats == null
                ? null
                : (simStats.sim_dashboard_by_operator || {
                    rows: [],
                    totals: {
                      operator: 'total',
                      label: 'Итого',
                      all: { total: 0, working: 0, reserve: 0, frozen: 0 },
                      msk: { total: 0, working: 0, reserve: 0, frozen: 0 },
                      spb: { total: 0, working: 0, reserve: 0, frozen: 0 },
                    },
                  })
            }
          />

          {/* Ряд 2 — 4 таблицы данных */}
          <DashboardDataBlock
            title="SIM-карты"
            count={simTotal}
            linkTo="/sim-manager"
            columns={['ГЕО', 'Количество', 'Резерв', 'Заморожено']}
            rows={[
              ['СПб', simSpbCount, reserveSpb, frozenSpb],
              ['Мск', simMskCount, reserveMsk, frozenMsk],
              ['Итого', simTotal, reserveTotal, frozenTotal],
            ]}
            hasTotalRow
          />
          <DashboardDataBlock
            title="Текущие оплаты SIM"
            linkTo="/sim-manager?payment=urgent"
            columns={['Срок', 'Количество', 'Сумма']}
            rows={[
              ['Сегодня', upcoming.today || 0, formatNum(urgencySums.red)],
              ['Завтра', upcoming.tomorrow || 0, formatNum(urgencySums.blue)],
              ['Всего за 3 дня', (upcoming.today || 0) + (upcoming.tomorrow || 0) + (upcoming['3days'] || 0), formatNum(urgencySums.total_3days || (urgencySums.red + urgencySums.blue + urgencySums.black))],
            ]}
            rowVariants={['red', 'blue', 'default']}
          />

          {/* Недоступные модемы */}
          {unavailableModems.length > 0 ? (
            <UnavailableModemsBlock
              title="Недоступные модемы"
              linkTo="/network-ports?status=not_working"
              modems={unavailableModems}
              onRefresh={fetchData}
              onRebootModem={handleRebootUnavailableModem}
              onRebootAll={handleRebootAllUnavailableModems}
              bulkRebootLoading={dashboardModemRebootLoading}
              isRebootDisabled={isDashboardModemRebootDisabled}
            />
          ) : null}

          <section className="dashboard-block dashboard-modem-mismatch" aria-label="Рассинхрон настроек модема">
            <div className="dashboard-block__title-row">
              <h2 className="dashboard-block__title">
                Рассинхрон настроек модема
              </h2>
              <div className="dashboard-modem-mismatch__actions">
                <button
                  type="button"
                  className="dashboard-link dashboard-modem-mismatch__check-btn"
                  onClick={handleCheckModemMismatch}
                  disabled={modemMismatchCheckLoading || modemMismatchBulkApplying}
                  aria-label="Проверить рассинхроны вручную"
                >
                  {modemMismatchCheckLoading ? 'Проверка...' : 'Проверить'}
                </button>
                <button
                  type="button"
                  className="dashboard-link dashboard-modem-mismatch__apply-btn"
                  onClick={handleApplySelectedDefaults}
                  disabled={
                    modemMismatchBulkApplying ||
                    modemMismatchCheckLoading ||
                    modemMismatchApplyingRowId !== null ||
                    selectedDefaultMismatchModemIds.length === 0
                  }
                  aria-label={`Применить дефолтные настройки к выбранным модемам (${selectedDefaultMismatchModemIds.length})`}
                >
                  {modemMismatchBulkApplying
                    ? 'Применяем выбранные...'
                    : 'Применить'}
                </button>
              </div>
            </div>

            <div className="dashboard-modem-mismatch__table-wrap">
              <table className="dashboard-modem-mismatch__table">
                <thead>
                  <tr>
                    <th>
                      <input
                        type="checkbox"
                        checked={allDefaultRowsSelected}
                        onChange={(event) => handleToggleSelectAllDefaults(event.target.checked)}
                        disabled={
                          modemMismatchBulkApplying ||
                          modemMismatchApplyingRowId !== null ||
                          defaultMismatchModemIds.length === 0
                        }
                        aria-label="Выбрать все строки с дефолтным рассинхроном"
                      />
                    </th>
                    <th>ID</th>
                    <th>Клиент</th>
                    <th>Порт</th>
                    <th>Подключение</th>
                    <th>Модем</th>
                    <th>Прокси ротация</th>
                    <th>Интервал прокси</th>
                    <th>Прокси OSFP</th>
                    <th>Модем ротация</th>
                    <th>Интервал модема</th>
                    <th>Модем OSFP</th>
                    <th>Статус</th>
                    <th>Действие</th>
                  </tr>
                </thead>
                <tbody>
                  {(modemMismatchLoading && modemMismatchRows.length === 0) ? (
                    <tr>
                      <td colSpan={14} className="dashboard-modem-mismatch__empty dashboard-modem-mismatch__empty--loading">
                        Данные загружаются...
                      </td>
                    </tr>
                  ) : modemMismatchRows.length === 0 ? (
                    <tr>
                      <td colSpan={14} className="dashboard-modem-mismatch__empty">
                        Расхождений не найдено
                      </td>
                    </tr>
                  ) : (
                    modemMismatchRows.map((row) => (
                      <tr key={row.row_id || row.proxy_id || `modem-${row.modem_id}`}>
                        <td>
                          {row.row_type === 'modem_default_mismatch' && Number(row.modem_id || 0) > 0 ? (
                            <input
                              type="checkbox"
                              checked={selectedDefaultMismatchModemIds.includes(Number(row.modem_id))}
                              onChange={(event) => handleToggleDefaultModemSelection(row.modem_id, event.target.checked)}
                              disabled={modemMismatchBulkApplying || modemMismatchApplyingRowId !== null}
                              aria-label={`Выбрать модем ${row.modem_id} для массового применения дефолта`}
                            />
                          ) : '—'}
                        </td>
                        <td>
                          {row.proxy_id ? (
                            <a
                              href={`/proxies?page=1&proxy_id=${row.proxy_id}`}
                              className="dashboard-link"
                              aria-label={`Открыть прокси ${row.proxy_id} в разделе Прокси`}
                            >
                              {row.proxy_id}
                            </a>
                          ) : '—'}
                        </td>
                        <td>{row.proxy_login || '—'}</td>
                        <td>{row.port_number ?? '—'}</td>
                        <td>{row.connection_name || '—'}</td>
                        <td>{buildModemDisplayName(row)}</td>
                        <td>{renderReconnectType(row.proxy_reconnect_type)}</td>
                        <td>{renderIntervalSeconds(row.proxy_reconnect_type, row.proxy_reconnect_interval)}</td>
                        <td>{row.proxy_osfp_label || '—'}</td>
                        <td>{renderReconnectType(row.modem_reconnect_type)}</td>
                        <td>{renderIntervalSeconds(row.modem_reconnect_type, row.modem_reconnect_interval)}</td>
                        <td>{row.modem_osfp_label || '—'}</td>
                        <td>{renderMismatchStatus(row)}</td>
                        <td>
                          <button
                            type="button"
                            className="dashboard-link dashboard-modem-mismatch__apply-btn"
                            onClick={() => handleApplyMismatchSettings(row)}
                            disabled={
                              modemMismatchBulkApplying ||
                              modemMismatchApplyingRowId === (row.row_id || row.proxy_id || `modem-${row.modem_id}`)
                            }
                            aria-label={
                              row.row_type === 'modem_default_mismatch'
                                ? `Применить дефолт к модему ${row.modem_id}`
                                : `Применить настройки для прокси ${row.proxy_id}`
                            }
                          >
                            {modemMismatchApplyingRowId === (row.row_id || row.proxy_id || `modem-${row.modem_id}`)
                              ? 'Применение...'
                              : 'Применить'}
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      ) : (
        <div
          id="dashboard-panel-sales"
          className="dashboard-tab-panel"
          role="tabpanel"
          aria-labelledby="dashboard-tab-sales"
        >
          <DashboardSalesPanel
            stats={stats}
            salesData={salesData}
            salesDataByGranularity={salesDataByGranularity}
            autoChartGranularity={autoChartGranularity}
            onEnsureChartGranularity={ensureSalesChartGranularity}
            geoFilter={salesFilters.geoFilter}
            periodFilter={salesFilters.periodFilter}
            rangeStart={salesFilters.rangeStart}
            rangeEnd={salesFilters.rangeEnd}
            visibleDataSections={visibleSalesDataSections}
          />
        </div>
      )}

    </div>
  );
};

export default Dashboard;
